import React from 'react';
import GlyphAtom from '../Glyph/GlyphAtom';
import './SchemaCardAtom.scss';

export interface SchemaCardAtomProps {
  /**
   * Glyph identifier. Accepts the schema's `glyph` field (Iconify `set:name`)
   * or a legacy avatar CSS class (normalized to Iconify by GlyphAtom).
   */
  icon?: string | null;
  /** Display label, already localized. */
  label: string;
  /** One-line description, already localized. Optional — falls back to nothing. */
  description?: string | null;
  /** Click handler — when set, the card becomes a button. */
  onClick?: () => void;
  className?: string;
  /** Test hook forwarded to the root element. */
  'data-testid'?: string;
}

const FALLBACK_ICON = 'lucide:circle';

/**
 * Schema tile card. Renders a schema's icon + label + short description
 * in a bordered card. Used to display a schema type as a gallery item
 * (e.g. the marketplace "Sphere Types Provided" bucket).
 *
 * The visual treatment matches AddTemplate's `schema-card` (the canonical
 * inline pattern in the app), but without the Preview/Use Template footer.
 */
const SchemaCardAtom: React.FC<SchemaCardAtomProps> = ({
  icon,
  label,
  description,
  onClick,
  className = '',
  'data-testid': dataTestId,
}) => {
  const iconClass = icon ?? FALLBACK_ICON;
  const classes = [
    'cp-schema-card',
    onClick ? 'cp-schema-card--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <div className="cp-schema-card__header">
        <GlyphAtom glyph={iconClass} className="cp-schema-card__icon" />
        <h4 className="cp-schema-card__label">{label}</h4>
      </div>
      {description && (
        <p className="cp-schema-card__description">{description}</p>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        aria-label={label}
        data-testid={dataTestId}
      >
        {content}
      </button>
    );
  }

  return (
    <article className={classes} data-testid={dataTestId}>
      {content}
    </article>
  );
};

SchemaCardAtom.displayName = 'SchemaCardAtom';

export default SchemaCardAtom;
